import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom'; // Importar ReactDOM para createPortal
import styles from './MultiDataPicker.module.css';

// --- Funções Utilitárias de Data ---

/**
 * Formata um objeto Date para o formato DD/MM/YYYY.
 * @param {Date} date - O objeto Date a ser formatado.
 * @returns {string} A data formatada como string.
 */
const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};


/**
 * Converte uma string de data (DD/MM/YYYY) para um objeto Date.
 * @param {string} dateString - A string da data.
 * @returns {Date} O objeto Date correspondente.
 * descontinuado
 */
// const parseDate = (dateString) => {
//     const [day, month, year] = dateString.split('/').map(Number);
//     return new Date(year, month - 1, day);
// };

/**
 * Cria um objeto Date representando a meia-noite do dia fornecido.
 * Isso é útil para normalizar datas e evitar problemas com fuso horário ou horas/minutos/segundos.
 * @param {Date} date - O objeto Date original.
 * @returns {Date} Um novo objeto Date configurado para a meia-noite.
 */
const createDateAtMidnight = (date) => {
    const d = new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Retorna o primeiro dia (Domingo) da semana para uma data específica, à meia-noite.
 * @param {Date} date - A data de referência.
 * @returns {Date} O objeto Date representando o início da semana.
 */
const getStartOfWeek = (date) => {
    const d = new Date(date);
    const dayOfWeek = d.getDay(); // 0 (Dom) - 6 (Sáb)
    const diff = d.getDate() - dayOfWeek;
    return createDateAtMidnight(new Date(d.setDate(diff)));
};

/**
 * Encontra a N-ésima ocorrência de um dia da semana em um mês específico.
 * @param {number} year - O ano.
 * @param {number} month - O mês (0-11).
 * @param {number} dayOfWeek - O dia da semana (0=Domingo, 1=Segunda, ...).
 * @param {number} n - A ocorrência desejada (1=primeira, 2=segunda, ...).
 * @returns {Date|null} O objeto Date da N-ésima ocorrência ou null se não encontrado.
 */
const getNthDayOfWeekInMonth = (year, month, dayOfWeek, n) => {
    // const firstOfMonth = new Date(year, month, 1);
    let count = 0;
    let targetDate = null;

    for (let day = 1; day <= 31; day++) {
        const currentDate = new Date(year, month, day);
        if (currentDate.getMonth() !== month) break; // Saiu do mês

        if (currentDate.getDay() === dayOfWeek) {
            count++;
            if (count === n) {
                targetDate = currentDate;
                break;
            }
        }
    }
    return targetDate ? createDateAtMidnight(targetDate) : null;
};

// --- ALTERAÇÃO: INÍCIO ---
// Nova função utilitária para encontrar o último dia da semana em um mês.
/**
 * Encontra a última ocorrência de um dia da semana em um mês específico.
 * @param {number} year - O ano.
 * @param {number} month - O mês (0-11).
 * @param {number} dayOfWeek - O dia da semana (0=Domingo, 1=Segunda, ...).
 * @returns {Date|null} O objeto Date da última ocorrência ou null se não encontrado.
 */
const getLastDayOfWeekInMonth = (year, month, dayOfWeek) => {
    const lastDayOfMonth = new Date(year, month + 1, 0); // Pega o último dia do mês
    const lastDayDate = lastDayOfMonth.getDate();

    for (let day = lastDayDate; day > 0; day--) {
        const currentDate = new Date(year, month, day);
        if (currentDate.getDay() === dayOfWeek) {
            return createDateAtMidnight(currentDate); // Encontrou, retorna a data
        }
    }
    return null; // Caso de segurança, não deve acontecer em um mês normal
};
// --- ALTERAÇÃO: FIM ---


// --- Configuração das Opções de Seleção Rápida ---
const quickSelectOptionsConfig = [
    { id: 'daily', label: 'Diariamente', unit: 'day' },
    { id: 'weekly', label: 'Semanalmente', unit: 'week' },
    { id: 'biweekly', label: 'Quinzenalmente', unit: 'biweekly' },
    { id: 'monthly', label: 'Mensalmente', unit: 'month' },
];


/**
 * Componente DropdownContent que renderiza a interface do calendário e seleção de datas.
 * Este componente é projetado para ser usado com ReactDOM.createPortal para evitar problemas de overflow.
 */
const CalendarDropdownContent = ({
    currentMonthDate,
    selectedDates,
    handleDateClick,
    removeDateFromList,
    navigateToDateInList,
    changeMonth,
    calendarDays,
    monthYearLabel,
    activeQuickSelectType,
    quickSelectCount,
    setQuickSelectCount,
    enabledQuickSelectTypes,
    handleQuickSelectTypeChange,
    applyAdvancedQuickSelect,
    clearAllSelections,
    monthlyReplicationStrategy,
    setMonthlyReplicationStrategy,
    activeQuickSelectLabel,
    onClose,
}) => {
    // Função para obter o portalRoot. Cria o elemento se não existir.
    const getPortalRoot = () => {
        let portalRoot = document.getElementById('datepicker-portal-root');
        if (!portalRoot) {
            portalRoot = document.createElement('div');
            portalRoot.setAttribute('id', 'datepicker-portal-root');
            // Estilos base do portal
            portalRoot.style.position = 'fixed';
            portalRoot.style.top = '0';
            portalRoot.style.left = '0';
            portalRoot.style.width = '100vw';
            portalRoot.style.height = '100vh';
            portalRoot.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'; // Fundo semi-transparente
            portalRoot.style.zIndex = '1050'; // Valor alto para ficar sobre outros elementos
            portalRoot.style.display = 'none'; // Começa escondido
            document.body.appendChild(portalRoot);
        }
        return portalRoot;
    };

    // Efeito para gerenciar a visibilidade e layout do portal.
    useEffect(() => {
        const portalNode = getPortalRoot(); // Garante que o nó exista

        // Ao montar/aparecer, mostra o portal e aplica estilos de centralização flex
        portalNode.style.display = 'flex';
        portalNode.style.alignItems = 'center';
        portalNode.style.justifyContent = 'center';

        // Função de limpeza para quando o componente for desmontado (dropdown fecha)
        return () => {
            // Apenas esconde o portal, não o remove do DOM
            if (portalNode) {
                portalNode.style.display = 'none';
            }
        };
    }, []); // Array de dependências vazio para executar apenas na montagem e desmontagem.

    // O conteúdo JSX do dropdown que será renderizado no portal
    const dropdownJsx = (
        <div className={styles.calendarDropdown}>
            <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    lineHeight: '1',
                    fontSize: '24px',
                    color: '#333',
                    zIndex: '10'
                }}
            >
                &times;
            </button>
            <div className={styles.calendarMain}>
                {/* Cabeçalho do Calendário */}
                <div className={styles.calendarHeader}>
                    <button type="button" onClick={() => changeMonth(-1)} className={styles.calendarNavButton} aria-label="Mês anterior">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                    </button>
                    <div className={styles.currentMonthYear}>{monthYearLabel}</div>
                    <button type="button" onClick={() => changeMonth(1)} className={styles.calendarNavButton} aria-label="Próximo mês">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                    </button>
                </div>
                {/* Dias da Semana */}
                <div className={styles.calendarWeekdays}>
                    <div>Dom</div><div>Seg</div><div>Ter</div><div>Qua</div><div>Qui</div><div>Sex</div><div>Sáb</div>
                </div>
                {/* Grade de Dias */}
                <div className={styles.calendarDays}>
                    {calendarDays.map(({ dayNumber, date, isOtherMonth }, index) => {
                        const dateAtMidnight = createDateAtMidnight(date).getTime();
                        const isSelected = selectedDates.includes(dateAtMidnight);
                        let dayClasses = `${styles.calendarDay} ${isOtherMonth ? styles.otherMonth : styles.currentMonth} ${isSelected ? styles.selected : ''}`;
                        return (
                            <div
                                key={`${formatDate(date)}-${index}`}
                                className={dayClasses}
                                onClick={!isOtherMonth ? () => handleDateClick(date) : undefined}
                                role={!isOtherMonth ? "button" : undefined}
                                tabIndex={!isOtherMonth ? 0 : -1}
                                aria-pressed={!isOtherMonth ? isSelected : undefined}
                                aria-label={!isOtherMonth ? `Selecionar ${formatDate(date)}` : undefined}
                            >
                                {dayNumber}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Divisor Vertical */}
            <div className={styles.verticalDivider}></div>

            {/* Painel Direito */}
            <div className={styles.rightPanel}>
                {/* Seção de Datas Selecionadas */}
                <div className={styles.selectedDatesSection}>
                    <h3 className={styles.sectionTitle}>Datas Selecionadas:</h3>
                    <ul className={`${styles.selectedDatesList} ${styles.customScrollbar}`}>
                        {selectedDates.length === 0 ? (
                            <li className={styles.selectedDateItemPlaceholder}>Nenhuma data selecionada</li>
                        ) : (
                            selectedDates.map(dateTimestamp => (
                                <li
                                    key={dateTimestamp}
                                    className={styles.selectedDateItem}
                                    onClick={() => navigateToDateInList(dateTimestamp)}
                                    title={`Ir para ${formatDate(new Date(dateTimestamp))} no calendário`}
                                >
                                    {formatDate(new Date(dateTimestamp))}
                                    <button
                                        type="button"
                                        className={styles.removeDateButton}
                                        onClick={(e) => { e.stopPropagation(); removeDateFromList(dateTimestamp); }}
                                        aria-label={`Remover data ${formatDate(new Date(dateTimestamp))}`}
                                    >
                                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>

                {/* Divisor Horizontal para Mobile */}
                <div className={styles.horizontalDividerMobile}></div>

                {/* Seção de Replicação Rápida */}
                <div className={styles.quickSelectSection}>
                    <h3 className={styles.sectionTitle}>Replicação Rápida:</h3>
                    <div className={styles.quickSelectTypeButtonsContainer}>
                        {quickSelectOptionsConfig.map(opt => (
                            <button
                                type="button"
                                key={opt.id}
                                className={`${styles.quickSelectTypeButton} ${activeQuickSelectType === opt.id ? styles.active : ''} ${!enabledQuickSelectTypes.includes(opt.id) ? styles.disabled : ''}`}
                                onClick={() => handleQuickSelectTypeChange(opt.id)}
                                disabled={!enabledQuickSelectTypes.includes(opt.id)}
                                title={!enabledQuickSelectTypes.includes(opt.id) ? "Opção não disponível com as datas atuais" : ""}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    {activeQuickSelectType && enabledQuickSelectTypes.includes(activeQuickSelectType) && (
                        <div className={styles.quickSelectControls}>
                            <div className={styles.quickSelectCountContainer}>
                                <label htmlFor="quickSelectCountInput" className={styles.quickSelectCountLabel}>
                                    Repetir ({activeQuickSelectLabel}):
                                </label>
                                <input
                                    type="number"
                                    id="quickSelectCountInput"
                                    className={styles.quickSelectCountInput}
                                    value={quickSelectCount}
                                    onChange={(e) => setQuickSelectCount(e.target.value)}
                                    min="1"
                                />
                            </div>

                            {activeQuickSelectType === 'monthly' && (
                                <div className={styles.monthlyStrategyContainer}>
                                    <label htmlFor="monthlyStrategySelect" className={styles.monthlyStrategyLabel}>Estratégia:</label>
                                    <select
                                        id="monthlyStrategySelect"
                                        className={styles.monthlyStrategySelect}
                                        value={monthlyReplicationStrategy}
                                        onChange={(e) => setMonthlyReplicationStrategy(e.target.value)}
                                    >
                                        <option value="dayNumber">Número do Dia</option>
                                        <option value="dayOfWeek">Dia da Semana (ex: 1ª Seg)</option>
                                    </select>
                                </div>
                            )}
                            <button
                                type="button"
                                className={`${styles.quickSelectBtn} ${styles.primary} ${styles.applyQuickSelectButton}`}
                                onClick={applyAdvancedQuickSelect}
                                disabled={selectedDates.length === 0 && !['daily', 'weekly', 'monthly'].includes(activeQuickSelectType)}
                            >
                                Aplicar
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        className={`${styles.quickSelectBtn} ${styles.secondary} ${styles.clearAllButton}`}
                        onClick={clearAllSelections}
                    >
                        Limpar Tudo
                    </button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(dropdownJsx, getPortalRoot());
};


/**
 * MultiDataPicker é um componente de seleção de múltiplas datas com calendário,
 * lista de datas selecionadas e opções de replicação rápida.
 *
 * @param {object} props - As propriedades do componente.
 * @param {function} props.onDatesChange - Callback chamado quando as datas selecionadas mudam. Recebe um array de objetos Date.
 * @param {Date[]} [props.initialSelectedDates=[]] - Array de datas inicialmente selecionadas.
 */
const MultiDataPicker = ({ onDatesChange, initialSelectedDates = [] }) => {
    // --- Estados do Componente ---
    const [selectedDates, setSelectedDates] = useState(() =>
        initialSelectedDates.map(date => createDateAtMidnight(date).getTime())
    );
    const [currentMonthDate, setCurrentMonthDate] = useState(new Date());
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [activeQuickSelectType, setActiveQuickSelectType] = useState(null);
    const [quickSelectCount, setQuickSelectCount] = useState(1);
    const [enabledQuickSelectTypes, setEnabledQuickSelectTypes] = useState(['daily', 'weekly', 'biweekly', 'monthly']);
    const [monthlyReplicationStrategy, setMonthlyReplicationStrategy] = useState('dayNumber');

    // --- Refs ---
    const datePickerRef = useRef(null);
    const inputRef = useRef(null);


    // --- Efeitos ---

    useEffect(() => {
        if (selectedDates.length > 0) {
            setInputValue(`${selectedDates.length} data(s) selecionada(s)`);
        } else {
            setInputValue('');
        }
        if (onDatesChange) {
            onDatesChange(selectedDates.map(time => new Date(time)));
        }
    }, [selectedDates, onDatesChange]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!isDropdownOpen) return;
            const portalRoot = document.getElementById('datepicker-portal-root');
            if (datePickerRef.current && datePickerRef.current.contains(event.target)) {
                return;
            }
            if (portalRoot && event.target === portalRoot) {
                handleCloseDropdown();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    useEffect(() => {
        let newEnabledTypes = ['daily', 'weekly', 'biweekly', 'monthly'];
        if (selectedDates.length > 0) {
            if (selectedDates.length === 1) {
                // Mantém todos habilitados
            } else {
                const datesObjects = selectedDates.map(ts => new Date(ts));
                const firstDate = datesObjects[0];
                const startOfWeekForFirstDate = getStartOfWeek(firstDate).getTime();
                const allInSameWeek = datesObjects.every(date => getStartOfWeek(date).getTime() === startOfWeekForFirstDate);

                if (allInSameWeek) {
                    newEnabledTypes = ['weekly', 'biweekly', 'monthly'];
                } else {
                    newEnabledTypes = ['monthly'];
                }
            }
        }
        setEnabledQuickSelectTypes(newEnabledTypes);
        if (activeQuickSelectType && !newEnabledTypes.includes(activeQuickSelectType)) {
            setActiveQuickSelectType(null);
        }
    }, [selectedDates, activeQuickSelectType]);


    // --- Handlers de Evento e Funções de Lógica ---

    const handleOpenDropdown = () => {
        if (selectedDates.length > 0) {
            const sortedDates = [...selectedDates].sort((a, b) => a - b);
            setCurrentMonthDate(new Date(sortedDates[0]));
        } else {
            setCurrentMonthDate(new Date());
        }
        setIsDropdownOpen(true);
    };

    const handleCloseDropdown = () => {
        setIsDropdownOpen(false);
    };

    const handleDateClick = useCallback((date) => {
        const dateAtMidnight = createDateAtMidnight(date).getTime();
        setSelectedDates(prevDates => {
            const newSelectedDatesSet = new Set(prevDates);
            if (newSelectedDatesSet.has(dateAtMidnight)) {
                newSelectedDatesSet.delete(dateAtMidnight);
            } else {
                newSelectedDatesSet.add(dateAtMidnight);
            }
            return Array.from(newSelectedDatesSet).sort((a, b) => a - b);
        });
    }, []);

    const removeDateFromList = useCallback((dateTimestamp) => {
        setSelectedDates(prevDates => prevDates.filter(d => d !== dateTimestamp));
    }, []);

    const navigateToDateInList = useCallback((dateTimestamp) => {
        const dateToNavigate = new Date(dateTimestamp);
        setCurrentMonthDate(new Date(dateToNavigate.getFullYear(), dateToNavigate.getMonth(), 1));
        setIsDropdownOpen(true);
    }, []);

    const changeMonth = (amount) => {
        setCurrentMonthDate(prevDate => {
            const newDate = new Date(prevDate);
            newDate.setMonth(newDate.getMonth() + amount);
            return newDate;
        });
    };

    const handleQuickSelectTypeChange = (typeId) => {
        if (!enabledQuickSelectTypes.includes(typeId)) return;
        if (activeQuickSelectType === typeId) {
            setActiveQuickSelectType(null);
        } else {
            setActiveQuickSelectType(typeId);
            setQuickSelectCount(1);
            if (typeId !== 'monthly') {
                setMonthlyReplicationStrategy('dayNumber');
            }
        }
    };

    const applyAdvancedQuickSelect = () => {
        if (!activeQuickSelectType || quickSelectCount < 1) {
            console.warn("Seleção rápida requer um tipo ativo e contagem válida.");
            return;
        }

        let baseDatesForReplication = selectedDates.map(ts => new Date(ts)).sort((a, b) => a - b);
        let newSelectedDatesTimestamps = new Set(selectedDates);
        const count = parseInt(quickSelectCount, 10);

        if (baseDatesForReplication.length === 0) {
            const today = createDateAtMidnight(new Date());
            if (activeQuickSelectType === 'daily') {
                for (let i = 0; i < count; i++) {
                    const nextDay = new Date(today);
                    nextDay.setDate(today.getDate() + i);
                    newSelectedDatesTimestamps.add(createDateAtMidnight(nextDay).getTime());
                }
            } else if (activeQuickSelectType === 'weekly') {
                for (let i = 0; i < count * 7; i++) {
                    const nextDay = new Date(today);
                    nextDay.setDate(today.getDate() + i);
                    newSelectedDatesTimestamps.add(createDateAtMidnight(nextDay).getTime());
                }
            } else if (activeQuickSelectType === 'monthly') {
                const originalDay = today.getDate();
                for (let i = 0; i < count; i++) {
                    const targetMonthDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
                    const daysInTargetMonth = new Date(targetMonthDate.getFullYear(), targetMonthDate.getMonth() + 1, 0).getDate();
                    const targetDay = Math.min(originalDay, daysInTargetMonth);
                    targetMonthDate.setDate(targetDay);
                    newSelectedDatesTimestamps.add(createDateAtMidnight(targetMonthDate).getTime());
                }
            }
            const finalDatesArray = Array.from(newSelectedDatesTimestamps).sort((a, b) => a - b);
            setSelectedDates(finalDatesArray);
            if (finalDatesArray.length > 0) {
                setCurrentMonthDate(new Date(finalDatesArray[0]));
            }
            return;
        }

        switch (activeQuickSelectType) {
            case 'daily':
                if (baseDatesForReplication.length > 0) {
                    const lastSelectedDate = baseDatesForReplication[baseDatesForReplication.length - 1];
                    for (let i = 1; i <= count; i++) {
                        const nextDay = new Date(lastSelectedDate);
                        nextDay.setDate(lastSelectedDate.getDate() + i);
                        newSelectedDatesTimestamps.add(createDateAtMidnight(nextDay).getTime());
                    }
                }
                break;
            case 'weekly':
                baseDatesForReplication.forEach(baseDate => {
                    for (let i = 1; i <= count; i++) {
                        const nextWeekDate = new Date(baseDate);
                        nextWeekDate.setDate(baseDate.getDate() + (i * 7));
                        newSelectedDatesTimestamps.add(createDateAtMidnight(nextWeekDate).getTime());
                    }
                });
                break;
            // --- ALTERAÇÃO: INÍCIO ---
            case 'biweekly':
                // Para cada data base, adiciona 'count' ocorrências a cada 15 dias.
                baseDatesForReplication.forEach(baseDate => {
                    for (let i = 1; i <= count; i++) {
                        const nextBiWeekDate = new Date(baseDate);
                        // Alterado de 14 para 15 para representar um salto quinzenal.
                        nextBiWeekDate.setDate(baseDate.getDate() + (i * 15));
                        newSelectedDatesTimestamps.add(createDateAtMidnight(nextBiWeekDate).getTime());
                    }
                });
                break;
            // --- ALTERAÇÃO: FIM ---
            case 'monthly':
                baseDatesForReplication.forEach(baseDate => {
                    for (let c = 1; c <= count; c++) {
                        const targetMonth = baseDate.getMonth() + c;
                        const targetYear = baseDate.getFullYear();

                        let replicatedDate;
                        if (monthlyReplicationStrategy === 'dayNumber') {
                            const originalDayOfMonth = baseDate.getDate();
                            const firstOfTargetMonth = new Date(targetYear, targetMonth, 1);
                            const daysInTargetMonth = new Date(firstOfTargetMonth.getFullYear(), firstOfTargetMonth.getMonth() + 1, 0).getDate();
                            const targetDay = Math.min(originalDayOfMonth, daysInTargetMonth);
                            replicatedDate = createDateAtMidnight(new Date(firstOfTargetMonth.getFullYear(), firstOfTargetMonth.getMonth(), targetDay));
                        } else { // 'dayOfWeek'
                            // --- ALTERAÇÃO: INÍCIO ---
                            const originalDayOfWeek = baseDate.getDay();

                            // Verifica se a data base é a última ocorrência do seu dia da semana no mês.
                            const nextWeekSameDay = new Date(baseDate);
                            nextWeekSameDay.setDate(baseDate.getDate() + 7);
                            const isLastOccurrence = nextWeekSameDay.getMonth() !== baseDate.getMonth();

                            const firstOfTargetMonth = new Date(targetYear, targetMonth, 1);
                            const targetMonthActual = firstOfTargetMonth.getMonth();
                            const targetYearActual = firstOfTargetMonth.getFullYear();

                            if (isLastOccurrence) {
                                // Se for a última ocorrência, busca a última do mês de destino.
                                replicatedDate = getLastDayOfWeekInMonth(
                                    targetYearActual,
                                    targetMonthActual,
                                    originalDayOfWeek
                                );
                            } else {
                                // Se não for a última, usa a lógica de N-ésima ocorrência.
                                let nthOccurrence = 0;
                                for (let d = 1; d <= baseDate.getDate(); d++) {
                                    if (new Date(baseDate.getFullYear(), baseDate.getMonth(), d).getDay() === originalDayOfWeek) {
                                        nthOccurrence++;
                                    }
                                }
                                replicatedDate = getNthDayOfWeekInMonth(
                                    targetYearActual,
                                    targetMonthActual,
                                    originalDayOfWeek,
                                    nthOccurrence
                                );
                            }
                            // --- ALTERAÇÃO: FIM ---
                        }
                        if (replicatedDate) {
                            newSelectedDatesTimestamps.add(replicatedDate.getTime());
                        }
                    }
                });
                break;
            default:
                break;
        }

        const finalSelectedArray = Array.from(newSelectedDatesTimestamps).sort((a, b) => a - b);
        setSelectedDates(finalSelectedArray);

        if (finalSelectedArray.length > 0 && isDropdownOpen) {
            setCurrentMonthDate(new Date(finalSelectedArray[0]));
        } else if (finalSelectedArray.length === 0) {
            setCurrentMonthDate(new Date());
        }
    };


    const clearAllSelections = () => {
        setSelectedDates([]);
        setCurrentMonthDate(new Date());
        setActiveQuickSelectType(null);
        setQuickSelectCount(1);
        setEnabledQuickSelectTypes(['daily', 'weekly', 'biweekly', 'monthly']);
        setMonthlyReplicationStrategy('dayNumber');
    };

    // --- Valores Memoizados ---

    const calendarDays = useMemo(() => {
        const year = currentMonthDate.getFullYear();
        const month = currentMonthDate.getMonth();
        const daysArray = [];

        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfPrevMonth = new Date(year, month, 0);
        const startDayOfWeek = firstDayOfMonth.getDay();
        for (let i = startDayOfWeek - 1; i >= 0; i--) {
            const day = lastDayOfPrevMonth.getDate() - i;
            daysArray.push({ dayNumber: day, date: new Date(year, month - 1, day), isOtherMonth: true });
        }

        const lastDayOfMonth = new Date(year, month + 1, 0);
        for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
            daysArray.push({ dayNumber: i, date: new Date(year, month, i), isOtherMonth: false });
        }

        const totalDaysDisplayed = daysArray.length;
        const remainingCells = (Math.ceil(totalDaysDisplayed / 7) * 7) - totalDaysDisplayed;
        for (let i = 1; i <= remainingCells; i++) {
            daysArray.push({ dayNumber: i, date: new Date(year, month + 1, i), isOtherMonth: true });
        }
        return daysArray;
    }, [currentMonthDate]);

    const monthYearLabel = useMemo(() =>
        new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(currentMonthDate),
        [currentMonthDate]
    );

    const activeQuickSelectLabel = useMemo(() =>
        activeQuickSelectType ? quickSelectOptionsConfig.find(opt => opt.id === activeQuickSelectType)?.label.toLowerCase() : '',
        [activeQuickSelectType]
    );


    // --- Renderização do Componente ---
    return (
        <div className={styles.datePickerContainer} ref={datePickerRef}>
            <div className={styles.inputWrapper}>
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Selecione as datas"
                    readOnly
                    className={styles.dateInputField}
                    value={inputValue}
                    onClick={handleOpenDropdown}
                    aria-haspopup="true"
                // aria-expanded={isDropdownOpen}
                />
                <div className={styles.inputIconContainer} onClick={handleOpenDropdown} role="button" aria-label="Abrir calendário">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                    </svg>
                </div>
            </div>

            {isDropdownOpen && (
                <CalendarDropdownContent
                    onClose={handleCloseDropdown}
                    currentMonthDate={currentMonthDate}
                    selectedDates={selectedDates}
                    handleDateClick={handleDateClick}
                    removeDateFromList={removeDateFromList}
                    navigateToDateInList={navigateToDateInList}
                    changeMonth={changeMonth}
                    calendarDays={calendarDays}
                    monthYearLabel={monthYearLabel}
                    activeQuickSelectType={activeQuickSelectType}
                    quickSelectCount={quickSelectCount}
                    setQuickSelectCount={setQuickSelectCount}
                    enabledQuickSelectTypes={enabledQuickSelectTypes}
                    handleQuickSelectTypeChange={handleQuickSelectTypeChange}
                    applyAdvancedQuickSelect={applyAdvancedQuickSelect}
                    clearAllSelections={clearAllSelections}
                    monthlyReplicationStrategy={monthlyReplicationStrategy}
                    setMonthlyReplicationStrategy={setMonthlyReplicationStrategy}
                    activeQuickSelectLabel={activeQuickSelectLabel}
                />
            )}
        </div>
    );
};


export default MultiDataPicker;